// Native device utilities for Capacitor
// These functions work on both web and native platforms

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { App } from '@capacitor/app';

// Check if running on native platform
export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform(); // 'ios', 'android', or 'web'

// ============ PUSH NOTIFICATIONS ============

export const initPushNotifications = async () => {
  if (!isNative()) {
    console.log('Push notifications not available on web');
    return null;
  }

  try {
    // Request permission
    const permStatus = await PushNotifications.requestPermissions();
    
    if (permStatus.receive === 'granted') {
      // Register with Apple/Google to receive push
      await PushNotifications.register();
      
      // Listen for registration token
      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration token:', token.value);
        // Send token to your backend to store
        return token.value;
      });

      // Listen for push notifications received while app is open
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push notification received:', notification);
      });

      // Listen for push notification action (user tapped on notification)
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('Push notification action performed:', notification);
        // Navigate based on notification data
        const data = notification.notification.data;
        if (data?.url) {
          window.location.href = data.url;
        }
      });

      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error initializing push notifications:', error);
    return null;
  }
};

// ============ STATUS BAR ============

export const setStatusBarStyle = async (style = 'dark') => {
  if (!isNative()) return;
  
  try {
    await StatusBar.setStyle({ 
      style: style === 'dark' ? Style.Dark : Style.Light 
    });
  } catch (error) {
    console.error('Error setting status bar style:', error);
  }
};

export const setStatusBarColor = async (color = '#1A3C34') => {
  if (!isNative() || getPlatform() !== 'android') return;
  
  try {
    await StatusBar.setBackgroundColor({ color });
  } catch (error) {
    console.error('Error setting status bar color:', error);
  }
};

export const hideStatusBar = async () => {
  if (!isNative()) return;
  
  try {
    await StatusBar.hide();
  } catch (error) {
    console.error('Error hiding status bar:', error);
  }
};

export const showStatusBar = async () => {
  if (!isNative()) return;
  
  try {
    await StatusBar.show();
  } catch (error) {
    console.error('Error showing status bar:', error);
  }
};

// ============ SPLASH SCREEN ============

export const hideSplashScreen = async () => {
  if (!isNative()) return;
  
  try {
    await SplashScreen.hide();
  } catch (error) {
    console.error('Error hiding splash screen:', error);
  }
};

export const showSplashScreen = async () => {
  if (!isNative()) return;
  
  try {
    await SplashScreen.show({
      showDuration: 2000,
      autoHide: true
    });
  } catch (error) {
    console.error('Error showing splash screen:', error);
  }
};

// ============ HAPTICS ============

export const hapticFeedback = async (style = 'light') => {
  if (!isNative()) return;
  
  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    }[style] || ImpactStyle.Light;
    
    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    console.error('Error with haptic feedback:', error);
  }
};

export const hapticNotification = async (type = 'success') => {
  if (!isNative()) return;
  
  try {
    await Haptics.notification({ type });
  } catch (error) {
    console.error('Error with haptic notification:', error);
  }
};

// ============ APP LIFECYCLE ============

export const initAppListeners = () => {
  if (!isNative()) return;

  // Handle app state changes
  App.addListener('appStateChange', ({ isActive }) => {
    console.log('App state changed. Is active?', isActive);
    if (isActive) {
      // App came to foreground
      // Refresh data, check notifications, etc.
    }
  });

  // Handle back button on Android
  App.addListener('backButton', ({ canGoBack }) => {
    if (!canGoBack) {
      // At root of navigation, minimize app instead of exiting
      App.minimizeApp();
    } else {
      window.history.back();
    }
  });

  // Handle deep links
  App.addListener('appUrlOpen', (event) => {
    console.log('Deep link:', event.url);
    // Parse the URL and navigate accordingly
    const slug = event.url.split('.app').pop();
    if (slug) {
      window.location.href = slug;
    }
  });
};

// ============ INITIALIZATION ============

export const initNativeApp = async () => {
  if (!isNative()) {
    console.log('Running on web platform');
    return;
  }

  console.log(`Running on ${getPlatform()} platform`);

  // Initialize app listeners
  initAppListeners();

  // Set status bar
  await setStatusBarStyle('dark');
  await setStatusBarColor('#1A3C34');

  // Initialize push notifications
  await initPushNotifications();

  // Hide splash screen after a short delay
  setTimeout(() => {
    hideSplashScreen();
  }, 500);
};

export default {
  isNative,
  getPlatform,
  initPushNotifications,
  setStatusBarStyle,
  setStatusBarColor,
  hideStatusBar,
  showStatusBar,
  hideSplashScreen,
  showSplashScreen,
  hapticFeedback,
  hapticNotification,
  initAppListeners,
  initNativeApp
};
